<script setup lang="ts">
import { ref } from 'vue';
import { PhoneNumberFormat } from 'google-libphonenumber';

const props = defineProps<{
    code: string;
    country: string;
}>();

const emit = defineEmits(['update:country']);

const localCountry = ref(props.country);

watch(
    () => props.country,
    (newValue) => {
        localCountry.value = newValue;
    },
);

watch(localCountry, (newValue) => {
    emit('update:country', newValue);
});

const { $phoneUtil }: any = useNuxtApp();
const submitStatus = ref<'not-submitted' | 'pending' | 'success' | 'failed'>('not-submitted');
const phone = ref('');
const phoneE164 = ref(props.code);

async function submitForm() {
    submitStatus.value = 'pending';
    try {
        const result: { success: boolean } = await $fetch('/api/campaign-invite', {
            method: 'POST',
            body: {
                phone: phoneE164.value,
                country: props.country.toLowerCase(),
                code: props.code,
            },
        });
        submitStatus.value = result.success ? 'success' : 'failed';
    } catch {
        submitStatus.value = 'failed';
    }
}

function onPhoneKeyUp() {
    try {
        const number = $phoneUtil.parse(phone.value, props.country);
        phoneE164.value = $phoneUtil.format(number, PhoneNumberFormat.E164);
        const formattedNumber = $phoneUtil.format(number, PhoneNumberFormat.INTERNATIONAL);
        phone.value = formattedNumber;
    } catch (error) {
        console.log(error);
    }
}
</script>

<template>
    <div
        class="flex-wrap mb-8 lg:mb-16 space-x-4 space-y-4 sm:flex-row sm:justify-center sm:space-y-0 sm:space-x-4"
    >
        <form
            v-if="['not-submitted', 'pending', 'failed'].includes(submitStatus)"
            name="invite"
            class="flex flex-col gap-8 p-6 mx-auto mb-8 mt-8 max-w-96 bg-white rounded-lg dark:bg-gray-800 dark:border-gray-700 sm:grid-cols-2"
            @submit.prevent="submitForm"
        >
            <div>
                <div
                    v-if="submitStatus === 'failed'"
                    class="p-2 mb-2 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400"
                    role="alert"
                >
                    <span class="font-medium">Error!</span>
                </div>
                <label
                    for="phone-number"
                    class="block mb-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                    >{{ $t('form.label.phone_number') }}</label
                >
                <div class="flex">
                    <PhonePrefixDropdown v-model="localCountry" />
                    <input
                        id="phone-number"
                        v-model="phone"
                        type="tel"
                        name="phone"
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-r-lg border-l-gray-100 dark:border-l-gray-700 border-l-2 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                        required
                        @input="onPhoneKeyUp"
                    />
                </div>
            </div>
            <button
                type="submit"
                :disabled="submitStatus === 'pending'"
                class="py-3 px-5 text-sm font-medium text-center text-white rounded-lg bg-primary-700 w-full hover:bg-primary-800 focus:ring-4 focus:outline-none focus:ring-primary-300 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800"
            >
                {{ $t('form.campaign.submit.label') }}
            </button>
        </form>

        <div
            v-else-if="submitStatus === 'success'"
            class="px-4 mx-auto mb-8 max-w-screen-xl lg:px-6"
        >
            <div class="flex my-8 justify-center">
                <a href="https://tiptappdl.onelink.me/4MT6/mainwebsitedliphone" target="_blank">
                    <img
                        class="h-10 mr-4"
                        src="https://a.storyblok.com/f/198708/135x40/2f88ce29d3/app-store-badge-en.svg"
                        alt="App store badge"
                    />
                </a>
                <a href="https://tiptappdl.onelink.me/4MT6/mainwebsitedlandroid" target="_blank">
                    <img
                        class="h-10 mr-4"
                        src="https://a.storyblok.com/f/198708/135x40/11b4537e93/google-play-badge-en.svg"
                        alt="Google play badge"
                    />
                </a>
            </div>
            <div class="max-w-2xl mx-auto text-center">
                <p class="mt-4 text-base font-normal text-white sm:text-xl">
                    {{ $t('form.campaign.submit_success.label') }}
                </p>
            </div>
        </div>
    </div>
</template>
